/**
 * @architeia/naive-a11y — KORA-156 (epic MM-772).
 *
 * Naive UI 2.44 leaves a set of accessibility gaps that every Naive app inherits: form-item labels
 * with no `for`, selects whose focus target is an unnamed `div`, a menu Tab skips entirely,
 * click-only pagination and checkable tags, card titles that are `role=heading` without a level, an
 * unnamed "add" button in dynamic tags, and toasts no screen reader hears. Wrapping every component
 * in every app would be a large, easily forgotten adoption job, so this fills the gaps where they
 * appear: one `installNaiveA11y()` call per app, watching the DOM and patching Naive's own markup.
 *
 * Everything here is additive and idempotent: an attribute the app already set (an explicit
 * `aria-label`, an `id`) is kept, and re-running a pass changes nothing. Vue only re-applies
 * attributes it renders itself when their bound value changes, so these additions persist.
 */

const PREFIX = 'naive-a11y'
const KEY_ACTIVATED = 'data-naive-a11y-key'

type Locale = 'en' | 'de'

const MESSAGES: Record<Locale, Record<string, string>> = {
  en: {
    add: 'Add',
    addTo: 'Add {label}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    jumpBack: 'Jump back several pages',
    jumpForward: 'Jump forward several pages',
    page: 'Page {page}',
    pageSize: 'Items per page'
  },
  de: {
    add: 'Hinzufügen',
    addTo: '{label} hinzufügen',
    previousPage: 'Vorherige Seite',
    nextPage: 'Nächste Seite',
    jumpBack: 'Mehrere Seiten zurück',
    jumpForward: 'Mehrere Seiten vor',
    page: 'Seite {page}',
    pageSize: 'Einträge pro Seite'
  }
}

function message(key: string, params: Record<string, string> = {}): string {
  const lang = document.documentElement.lang.toLowerCase().startsWith('de') ? 'de' : 'en'
  const template = MESSAGES[lang][key] ?? MESSAGES.en[key] ?? key
  return template.replace(/\{(\w+)\}/g, (_, name: string) => params[name] ?? '')
}

let idCounter = 0
function ensureId(element: Element): string {
  if (!element.id) element.id = `${PREFIX}-${++idCounter}`
  return element.id
}

function setIfChanged(element: Element, name: string, value: string | null): void {
  if (value === null) {
    if (element.hasAttribute(name)) element.removeAttribute(name)
  } else if (element.getAttribute(name) !== value) {
    element.setAttribute(name, value)
  }
}

function hasName(element: Element): boolean {
  return Boolean(
    element.getAttribute('aria-label')?.trim() || element.getAttribute('aria-labelledby')?.trim()
  )
}

/** Marks a click-only element as a keyboard-operable control: Enter and Space click it. */
function makeActivatable(element: HTMLElement, role: string, disabled: boolean): void {
  setIfChanged(element, 'role', role)
  setIfChanged(element, 'tabindex', disabled ? null : '0')
  setIfChanged(element, 'aria-disabled', disabled ? 'true' : null)
  element.setAttribute(KEY_ACTIVATED, '')
}

/**
 * A Naive select's focusable parts: the wrapper Tab lands on (`tabindex=0` on the label or the tag
 * list) and, when filterable, the inner input that takes over while typing (`tabindex=-1`). The
 * first is the combobox; both carry its name.
 */
function selectionParts(
  selection: Element
): { combobox: HTMLElement; inputs: HTMLElement[] } | null {
  const inputs = [...selection.querySelectorAll<HTMLElement>('input.n-base-selection-input')]
  const wrapper = selection.querySelector<HTMLElement>(
    '.n-base-selection-tags[tabindex="0"], .n-base-selection-label[tabindex="0"]'
  )
  const combobox = wrapper ?? inputs[0]
  if (!combobox) return null
  return { combobox, inputs: inputs.filter(input => input !== combobox) }
}

/** The control a form-item label should name, in the order Naive renders them. */
function formItemControl(blank: Element): HTMLElement | null {
  const selection = blank.querySelector('.n-base-selection')
  if (selection) return selectionParts(selection)?.combobox ?? null
  return blank.querySelector<HTMLElement>(
    [
      'input:not([type="hidden"]):not([tabindex="-1"])',
      'textarea',
      '[role="switch"]',
      '[role="radiogroup"]',
      '[role="slider"]'
    ].join(',')
  )
}

function labelFormItems(root: ParentNode): void {
  for (const item of root.querySelectorAll('.n-form-item')) {
    const label = item.querySelector(':scope > .n-form-item-label')
    const blank = item.querySelector(':scope > .n-form-item-blank')
    if (!label || !blank || !label.textContent?.trim()) continue
    const control = formItemControl(blank)
    if (!control || hasName(control)) continue
    const labelId = ensureId(label)
    if (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement) {
      // Re-pointed every pass: a `v-if` can swap the control under the same label.
      setIfChanged(label, 'for', ensureId(control))
    } else {
      control.setAttribute('aria-labelledby', labelId)
    }
  }
}

function enhanceSelects(root: ParentNode): void {
  for (const select of root.querySelectorAll('.n-select')) {
    const selection = select.querySelector('.n-base-selection')
    const parts = selection && selectionParts(selection)
    if (!selection || !parts) continue
    const { combobox, inputs } = parts
    setIfChanged(combobox, 'role', 'combobox')
    setIfChanged(combobox, 'aria-haspopup', 'listbox')
    setIfChanged(
      combobox,
      'aria-expanded',
      selection.classList.contains('n-base-selection--active') ? 'true' : 'false'
    )
    if (!hasName(combobox)) {
      const explicit = select.getAttribute('aria-label')
      const placeholder = selection
        .querySelector('.n-base-selection-placeholder__inner, .n-base-selection-overlay__wrapper')
        ?.textContent?.trim()
      const name =
        explicit ?? (select.closest('.n-pagination') ? message('pageSize') : placeholder || null)
      if (name) combobox.setAttribute('aria-label', name)
    }
    for (const input of inputs) {
      const labelledBy = combobox.getAttribute('aria-labelledby')
      const label = combobox.getAttribute('aria-label')
      if (labelledBy) setIfChanged(input, 'aria-labelledby', labelledBy)
      else if (label) setIfChanged(input, 'aria-label', label)
    }
    // The caret/loading suffix is announced as an image named "loading" even when nothing loads.
    for (const suffix of selection.querySelectorAll('.n-base-suffix[role="img"]')) {
      if (!suffix.querySelector('.n-base-loading__icon')) suffix.setAttribute('aria-hidden', 'true')
    }
  }
  // An `aria-label` an app passes to NInput lands on its wrapper, not on the input itself.
  for (const input of root.querySelectorAll('.n-input[aria-label]')) {
    const field = input.querySelector('input, textarea')
    if (field && !hasName(field))
      field.setAttribute('aria-label', input.getAttribute('aria-label')!)
  }
}

function enhanceMenus(root: ParentNode): void {
  for (const item of root.querySelectorAll<HTMLElement>('.n-menu-item[role="menuitem"]')) {
    const content = item.querySelector('.n-menu-item-content')
    if (!content) continue
    makeActivatable(item, 'menuitem', content.classList.contains('n-menu-item-content--disabled'))
    setIfChanged(
      item,
      'aria-current',
      content.classList.contains('n-menu-item-content--selected') ? 'page' : null
    )
  }
  // A horizontal menu's group opens a dropdown rather than nesting its items, so the group item is
  // a popup button and its wrapper is not a menu of its own.
  for (const submenu of root.querySelectorAll('.n-menu--horizontal .n-submenu')) {
    const trigger = submenu.querySelector<HTMLElement>(':scope > .n-menu-item[role="menuitem"]')
    if (!trigger) continue
    setIfChanged(submenu, 'role', 'none')
    setIfChanged(submenu, 'aria-expanded', null)
    setIfChanged(trigger, 'aria-haspopup', 'menu')
    setIfChanged(trigger, 'aria-expanded', String(isSubmenuOpen(trigger)))
  }
}

/** NMenu marks a group's content as hovered while its dropdown is open. */
function isSubmenuOpen(trigger: Element): boolean {
  return !!trigger.querySelector(':scope > .n-menu-item-content--hover')
}

function enhancePagination(root: ParentNode): void {
  for (const pagination of root.querySelectorAll('.n-pagination')) {
    const items = [...pagination.querySelectorAll<HTMLElement>('.n-pagination-item')]
    const buttons = items.filter(item => item.classList.contains('n-pagination-item--button'))
    const activeIndex = items.findIndex(item =>
      item.classList.contains('n-pagination-item--active')
    )
    items.forEach((item, index) => {
      const disabled = item.classList.contains('n-pagination-item--disabled')
      makeActivatable(item, 'button', disabled)
      let label: string
      if (item === buttons[0]) label = message('previousPage')
      else if (item === buttons[buttons.length - 1]) label = message('nextPage')
      else if (item.classList.contains('n-pagination-item--clickable'))
        label = message('page', { page: item.textContent?.trim() ?? '' })
      else label = message(index < activeIndex ? 'jumpBack' : 'jumpForward')
      setIfChanged(item, 'aria-label', label)
      setIfChanged(
        item,
        'aria-current',
        item.classList.contains('n-pagination-item--active') ? 'page' : null
      )
    })
  }
}

function enhanceTags(root: ParentNode): void {
  for (const tag of root.querySelectorAll<HTMLElement>('.n-tag--checkable')) {
    makeActivatable(tag, 'checkbox', tag.classList.contains('n-tag--disabled'))
    setIfChanged(tag, 'aria-checked', tag.classList.contains('n-tag--checked') ? 'true' : 'false')
  }
  for (const button of root.querySelectorAll('.n-dynamic-tags .n-button')) {
    if (hasName(button) && !button.hasAttribute(`data-${PREFIX}-named`)) continue
    if (button.textContent?.trim()) continue
    // Several lists on one form each get their own "+": name it after the field it adds to.
    const label = button
      .closest('.n-form-item')
      ?.querySelector(':scope > .n-form-item-label')
      ?.textContent?.replace(/\*\s*$/, '')
      .trim()
    setIfChanged(button, 'aria-label', label ? message('addTo', { label }) : message('add'))
    button.setAttribute(`data-${PREFIX}-named`, '')
  }
}

/** A form item in an error state marks its control invalid and points it at the message. */
function enhanceValidation(root: ParentNode): void {
  for (const item of root.querySelectorAll('.n-form-item')) {
    const blank = item.querySelector(':scope > .n-form-item-blank')
    if (!blank) continue
    const control = formItemControl(blank)
    if (!control) continue
    const feedback = item.querySelector('.n-form-item-feedback--error')
    const invalid = blank.classList.contains('n-form-item-blank--error')
    const ours = control.getAttribute(`data-${PREFIX}-describedby`)
    if (invalid && feedback?.textContent?.trim()) {
      const id = ensureId(feedback)
      setIfChanged(control, 'aria-invalid', 'true')
      setIfChanged(control, `data-${PREFIX}-invalid`, '')
      if (!control.hasAttribute('aria-describedby') || ours !== null) {
        setIfChanged(control, 'aria-describedby', id)
        setIfChanged(control, `data-${PREFIX}-describedby`, id)
      }
    } else {
      if (control.hasAttribute(`data-${PREFIX}-invalid`)) {
        control.removeAttribute('aria-invalid')
        control.removeAttribute(`data-${PREFIX}-invalid`)
      }
      if (ours !== null) {
        if (control.getAttribute('aria-describedby') === ours) {
          control.removeAttribute('aria-describedby')
        }
        control.removeAttribute(`data-${PREFIX}-describedby`)
      }
    }
  }
}

function enhanceCards(root: ParentNode): void {
  for (const header of root.querySelectorAll('.n-card-header[role="heading"]')) {
    // Naive nests a heading in a heading; only the title itself is one.
    header.removeAttribute('role')
  }
  for (const title of root.querySelectorAll('.n-card-header__main[role="heading"]')) {
    setIfChanged(title, 'aria-level', headingLevel(title.closest('.n-card')))
  }
  // NResult's title is plain text; an app that uses it as the page heading opts in with the level.
  for (const result of root.querySelectorAll('.n-result[data-heading-level]')) {
    const title = result.querySelector('.n-result-header__title')
    if (!title) continue
    setIfChanged(title, 'role', 'heading')
    setIfChanged(title, 'aria-level', headingLevel(result))
  }
}

/** An app marks the card (or result) that titles the page with `data-heading-level="1"`. */
function headingLevel(element: Element | null): string {
  const level = Number(element?.getAttribute('data-heading-level'))
  return Number.isInteger(level) && level >= 1 && level <= 6 ? String(level) : '2'
}

function enhance(root: ParentNode): void {
  labelFormItems(root)
  enhanceSelects(root)
  enhanceMenus(root)
  enhancePagination(root)
  enhanceTags(root)
  enhanceCards(root)
  enhanceValidation(root)
}

function createLiveRegion(role: 'status' | 'alert'): HTMLElement {
  const region = document.createElement('div')
  region.setAttribute('role', role)
  region.setAttribute('aria-live', role === 'alert' ? 'assertive' : 'polite')
  region.setAttribute('aria-atomic', 'true')
  region.setAttribute('data-naive-a11y-live', role)
  region.style.cssText =
    'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0'
  document.body.appendChild(region)
  return region
}

/** Toasts render into a container created on first use, too late to be a live region, so their
 *  text is mirrored into regions that exist from the start. Errors and warnings interrupt. */
function announceMessages(
  added: Iterable<Node>,
  polite: HTMLElement,
  assertive: HTMLElement
): void {
  for (const node of added) {
    if (!(node instanceof Element)) continue
    const messages = node.matches('.n-message') ? [node] : [...node.querySelectorAll('.n-message')]
    for (const toast of messages) {
      const text = toast.querySelector('.n-message__content')?.textContent?.trim()
      if (!text) continue
      const urgent =
        toast.classList.contains('n-message--error-type') ||
        toast.classList.contains('n-message--warning-type')
      const region = urgent ? assertive : polite
      // Clearing first makes a repeated identical message announce again.
      region.textContent = ''
      setTimeout(() => {
        region.textContent = text
      }, 50)
    }
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Enter' && event.key !== ' ') return
  const target = event.target
  if (!(target instanceof HTMLElement) || !target.hasAttribute(KEY_ACTIVATED)) return
  if (target.getAttribute('aria-disabled') === 'true') return
  // While a group's dropdown is open, Enter picks its highlighted option; the dropdown handles that.
  if (target.getAttribute('aria-haspopup') === 'menu' && isSubmenuOpen(target)) return
  event.preventDefault()
  // NMenu listens on the item's content element, the others on the element itself.
  const clickTarget = target.querySelector<HTMLElement>(':scope > .n-menu-item-content') ?? target
  clickTarget.click()
}

export interface NaiveA11yOptions {
  /** Where to look for Naive markup. Defaults to the whole document. */
  root?: HTMLElement
}

/**
 * Starts filling Naive UI's accessibility gaps under `root` and keeps doing so as the DOM changes.
 * Returns a function that stops it and removes the live regions it added.
 */
export function installNaiveA11y(options: NaiveA11yOptions = {}): () => void {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return () => {}
  const root = options.root ?? document.body
  const polite = createLiveRegion('status')
  const assertive = createLiveRegion('alert')

  let scheduled = false
  const schedule = (): void => {
    if (scheduled) return
    scheduled = true
    queueMicrotask(() => {
      scheduled = false
      observer.takeRecords()
      enhance(root)
      observer.takeRecords()
    })
  }

  const observer = new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'childList') announceMessages(record.addedNodes, polite, assertive)
    }
    schedule()
  })
  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class']
  })
  root.addEventListener('keydown', onKeydown)
  enhance(root)

  return () => {
    observer.disconnect()
    root.removeEventListener('keydown', onKeydown)
    polite.remove()
    assertive.remove()
  }
}

/** For tests: runs one enhancement pass synchronously. */
export const enhanceNaiveMarkup = enhance
